import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	AlertCircle,
	UserCircle,
	CheckCircle2,
	Copy,
	ArrowLeft,
	Globe,
	Link2,
} from 'lucide-react';
import { PageTitle } from '@/components/ui/PageTitle';
import {
	ProfessionalForm,
	ProfessionalDetailTags,
	professionalSchema,
	PermissionTagsEditor,
} from './components';
import {
	useGetClinics,
	useUpdateProfessional,
	useGetProfessional,
	useUpdateProfessionalAuthorization,
	useUpdateProfessionalPermissionTags,
	useUpdateProfessionalVisibility,
	useGetServices,
} from '@/services';
import { ensureDefaultPermissionTags } from '@/utils/professionalPermissionTags';
import { PROFESSIONAL_VISIBILITY_OPTIONS } from '@/constants/professional/visibility';
import { useQueryClient } from '@tanstack/react-query';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components';
import { formatDateWithTime } from '@/utils/transformer';
import { SERVICE_MODALITY_TRANSLATIONS } from '@/constants/professional/translations';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import { formatProfessionalFullName } from '@/utils';
import { z } from 'zod';

const AUTHORIZED_OPTIONS = [
	{ label: 'Sim', value: true },
	{ label: 'Não', value: false },
] as const;

const SlidingAuthorizedToggle = ({
	value,
	onChange,
	disabled,
}: {
	value: boolean;
	onChange: (val: boolean) => void;
	disabled?: boolean;
}) => {
	const activeIndex = AUTHORIZED_OPTIONS.findIndex((o) => o.value === value);
	const count = AUTHORIZED_OPTIONS.length;

	return (
		<div className="relative flex w-full rounded-lg bg-stone-100 p-1">
			<div
				className="absolute bottom-1 top-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
				style={{
					width: `calc((100% - 8px) / ${count})`,
					left: `calc(4px + ${activeIndex} * ((100% - 8px) / ${count}))`,
				}}
			/>
			{AUTHORIZED_OPTIONS.map((opt) => (
				<button
					key={String(opt.value)}
					type="button"
					disabled={disabled}
					onClick={() => onChange(opt.value)}
					className={cn(
						'z-10 flex-1 cursor-pointer rounded-md border-none bg-transparent px-0 py-1.5 text-center text-[10px] font-black uppercase tracking-tight outline-none transition-colors duration-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
						value === opt.value
							? 'text-stone-900'
							: 'text-stone-400 hover:text-stone-600',
					)}
				>
					{opt.label}
				</button>
			))}
		</div>
	);
};

const SlidingVisibilityToggle = ({
	value,
	onChange,
	disabled,
}: {
	value: Professionals.Visibility;
	onChange: (val: Professionals.Visibility) => void;
	disabled?: boolean;
}) => {
	const activeIndex = PROFESSIONAL_VISIBILITY_OPTIONS.findIndex(
		(option) => option.value === value,
	);
	const count = PROFESSIONAL_VISIBILITY_OPTIONS.length;

	return (
		<div className="relative flex w-full rounded-lg bg-stone-100 p-1">
			<div
				className="absolute bottom-1 top-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
				style={{
					width: `calc((100% - 8px) / ${count})`,
					left: `calc(4px + ${activeIndex} * ((100% - 8px) / ${count}))`,
				}}
			/>
			{PROFESSIONAL_VISIBILITY_OPTIONS.map((option) => {
				const Icon = option.value === 'PUBLIC_SEARCH' ? Globe : Link2;
				return (
					<div
						key={option.value}
						className="relative z-10 flex min-w-0 flex-1"
					>
						<Tooltip
							content={option.hint}
							position="top"
							className="max-w-[16rem] whitespace-normal text-left leading-snug"
						>
							<button
								type="button"
								disabled={disabled}
								onClick={() => onChange(option.value)}
								className={cn(
									'flex w-full cursor-pointer items-center justify-center rounded-md border-none bg-transparent px-0 py-2 outline-none transition-colors duration-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
									value === option.value
										? 'text-stone-900'
										: 'text-stone-400 hover:text-stone-600',
								)}
								aria-label={option.hint}
							>
								<Icon
									className="size-4"
									strokeWidth={2.25}
									aria-hidden
								/>
							</button>
						</Tooltip>
					</div>
				);
			})}
		</div>
	);
};

const EditProfessional = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const queryClient = useQueryClient();

	const { mutate, isPending } = useUpdateProfessional(() => {
		queryClient.invalidateQueries({ queryKey: ['professional', id] });
	});

	const { mutate: mutateAuthorization, isPending: isAuthorizing } =
		useUpdateProfessionalAuthorization(() => {
			queryClient.invalidateQueries({ queryKey: ['professional', id] });
		});

	const {
		mutate: mutatePermissionTags,
		isPending: isUpdatingPermissionTags,
	} = useUpdateProfessionalPermissionTags(() => {
		queryClient.invalidateQueries({ queryKey: ['professional', id] });
	});

	const { mutate: mutateVisibility, isPending: isUpdatingVisibility } =
		useUpdateProfessionalVisibility(() => {
			queryClient.invalidateQueries({ queryKey: ['professional', id] });
		});

	const handleChangeAuthorized = (authorized: boolean) => {
		if (!data) return;
		mutateAuthorization({ body: { authorized }, id: data.id });
	};

	const handlePermissionTagsChange = (
		permission_tags: Professionals.PermissionTag[],
	) => {
		if (!data) return;
		mutatePermissionTags({
			body: {
				permission_tags: ensureDefaultPermissionTags(permission_tags),
			},
			id: data.id,
		});
	};

	const handleChangeVisibility = (visibility: Professionals.Visibility) => {
		if (!data) return;
		mutateVisibility({ body: { visibility }, id: data.id });
	};

	const onSubmit = (data: z.infer<typeof professionalSchema>) => {
		const { email, profile_photo_url, ...body } = data;
		mutate(
			{ body, id: id as string },
			{ onSuccess: () => navigate('/admin/profissionais') },
		);
	};

	const handleChangeService = (serviceId: string) => {
		if (!serviceId || !id || serviceId === currentServiceId) return;
		mutate({
			body: {
				service_ids: [serviceId],
			} as Partial<Professionals.Professional>,
			id,
		});
	};

	const { data, isLoading, isError } = useGetProfessional(id ?? '', {
		enabled: !!id,
	});

	const { data: clinicsData } = useGetClinics(
		{ status: 'true' },
		{ enabled: !!data },
	);
	const { data: servicesData } = useGetServices({ enabled: !!data });

	const clinics = clinicsData?.data ?? [];
	const professionServices = (servicesData ?? []).filter(
		(s) => s.type === 'PROFESSION',
	);
	const currentServiceId =
		data?.professional_services?.[0]?.service?.id ?? '';
	const currentServiceName =
		data?.professional_services?.[0]?.service?.service_name ??
		data?.professional_services?.[0]?.service?.professional_label ??
		null;
	const clinicName =
		data &&
		'clinic' in data &&
		data.clinic &&
		typeof data.clinic === 'object'
			? (data.clinic as { name?: string })?.name
			: null;
	const clinicDisplay = clinicName ?? 'Online';

	const [copied, setCopied] = useState(false);
	const copyId = () => {
		if (data?.id) {
			navigator.clipboard.writeText(data.id);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="mx-auto max-w-5xl space-y-8">
				{/* Header */}
				<div className="flex items-center gap-6">
					<button
						type="button"
						onClick={() => navigate('/admin/profissionais')}
						className="group flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-stone-100 bg-white transition-all hover:border-stone-200 hover:shadow-sm"
					>
						<ArrowLeft
							size={20}
							className="text-stone-400 group-hover:text-stone-900"
						/>
					</button>
					<div className="flex min-w-0 flex-1 flex-col gap-1">
						<PageTitle
							text={
								data
									? `Profissional: ${formatProfessionalFullName(
											data,
									  )}`
									: 'Profissional'
							}
						/>
						<nav className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
							<span>PROFISSIONAIS</span>
							<span className="text-zinc-200">/</span>
							<span className="text-zinc-900">EDITAR</span>
						</nav>
					</div>
				</div>

				{isLoading ? (
					<div className="flex h-40 items-center justify-center">
						<p className="animate-pulse text-sm font-bold uppercase tracking-widest text-zinc-400">
							Carregando...
						</p>
					</div>
				) : isError || !data ? (
					<div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-zinc-200 bg-white py-24 text-center">
						<div className="mb-4 rounded-full bg-zinc-50 p-4 text-zinc-400 shadow-sm">
							<AlertCircle size={32} />
						</div>
						<h3 className="text-base font-bold text-zinc-900">
							Profissional não encontrado
						</h3>
						<p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-zinc-400">
							Não foi possível localizar os dados deste
							profissional.
						</p>
					</div>
				) : (
					<>
						{/* Informações do profissional */}
						<div className="overflow-hidden rounded-3xl bg-white shadow-sm">
							<div className="space-y-6 p-6 md:p-8">
								<div className="flex flex-col gap-3 md:gap-4">
									<div className="w-full space-y-4">
										<div className="flex flex-wrap items-center gap-2 text-zinc-400">
											<h2 className="text-lg font-bold text-zinc-900">
												Informações do profissional
											</h2>
											<div className="flex items-center gap-1.5 rounded-md border border-zinc-100 bg-zinc-50 px-2 py-1">
												<span className="font-mono text-xs">
													Id: {data.id}
												</span>
												<button
													type="button"
													onClick={copyId}
													className="transition-colors hover:text-zinc-900"
													aria-label="Copiar ID"
												>
													{copied ? (
														<CheckCircle2
															size={14}
															className="text-emerald-500"
														/>
													) : (
														<Copy size={14} />
													)}
												</button>
											</div>
										</div>

										<div className="flex items-center gap-5">
											<div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-zinc-200 bg-zinc-100 text-zinc-400">
												{data.profile_photo_url ? (
													<img
														src={
															data.profile_photo_url
														}
														alt=""
														className="h-full w-full object-cover"
														referrerPolicy="no-referrer"
													/>
												) : (
													<UserCircle size={32} />
												)}
											</div>
											<div className="min-w-0 space-y-1">
												<h3 className="text-base font-bold text-zinc-900">
													{formatProfessionalFullName(
														data,
													)}
												</h3>
												<p className="truncate text-xs text-zinc-500">
													{data.email ?? '-'}
												</p>
												{data.crm_or_crp && (
													<p className="font-mono text-[10px] text-zinc-400">
														crm ou crp:{' '}
														{data.crm_or_crp}
													</p>
												)}
											</div>
										</div>
									</div>

									<div className="flex w-full flex-1 flex-col gap-3">
										<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
											Serviço
										</p>
										<ProfessionalDetailTags
											serviceModality={
												data.service_modality
													? SERVICE_MODALITY_TRANSLATIONS[
															data
																.service_modality
													  ]
													: undefined
											}
											clinicName={clinicDisplay}
											serviceName={
												currentServiceName ?? ''
											}
										/>
									</div>

									<div className="flex w-full flex-1 flex-col gap-3">
										<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
											Acesso
										</p>
										<ProfessionalDetailTags
											createdAt={
												data.created_at
													? formatDateWithTime(
															data.created_at,
													  )
													: undefined
											}
											last_login={
												data.last_login
													? formatDateWithTime(
															data.last_login,
													  )
													: undefined
											}
											updatedAt={
												data.updated_at
													? formatDateWithTime(
															data.updated_at,
													  )
													: undefined
											}
										/>
									</div>

									<div className="flex w-full flex-1 flex-col gap-3">
										<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
											Permissões
										</p>
										<ProfessionalDetailTags
											authorized={
												data.authorized ?? undefined
											}
											googlePermissionsAllowed={
												data.google_permissions_allowed ??
												false
											}
											verifiedEmail={
												data.verified_email ?? false
											}
										/>
									</div>
								</div>
							</div>
						</div>

						{/* Serviço e autorização */}
						<div className="overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-sm">
							<div className="p-6 md:p-8">
								<h3 className="mb-2 text-lg font-bold text-zinc-900">
									Serviço e autorização
								</h3>
								<div className="space-y-6">
									<div>
										<p className="mb-4 text-sm text-zinc-500">
											Selecione o serviço oferecido por
											este profissional (apenas um) e
											defina se está autorizado a atender.
										</p>
										<div className="flex flex-col gap-4 sm:flex-row sm:items-end">
											<div className="min-w-0 flex-1 sm:max-w-md">
												<label className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-zinc-400">
													Serviço
												</label>
												<Select
													value={
														currentServiceId ||
														undefined
													}
													onValueChange={
														handleChangeService
													}
													disabled={isPending}
												>
													<SelectTrigger className="w-full">
														<SelectValue>
															{currentServiceId
																? professionServices.find(
																		(s) =>
																			s.id ===
																			currentServiceId,
																  )
																		?.service_name ??
																  currentServiceName
																: 'Selecione um serviço'}
														</SelectValue>
													</SelectTrigger>
													<SelectContent>
														{professionServices.map(
															(s) => (
																<SelectItem
																	key={s.id}
																	value={s.id}
																	className={cn(
																		s.id ===
																			currentServiceId &&
																			'cursor-default opacity-60 hover:bg-transparent',
																	)}
																	{...(s.id ===
																		currentServiceId && {
																		onClick:
																			(
																				e: React.MouseEvent,
																			) =>
																				e.preventDefault(),
																	})}
																>
																	{
																		s.service_name
																	}
																</SelectItem>
															),
														)}
													</SelectContent>
												</Select>
											</div>
											<div className="min-w-0 sm:w-40">
												<label className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-zinc-400">
													Autorizado
												</label>
												<SlidingAuthorizedToggle
													value={
														data.authorized ?? false
													}
													onChange={
														handleChangeAuthorized
													}
													disabled={isAuthorizing}
												/>
											</div>
											<div className="min-w-0 sm:w-28">
												<label className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-zinc-400">
													Visibilidade
												</label>
												<SlidingVisibilityToggle
													value={
														data.visibility ??
														'PUBLIC_SEARCH'
													}
													onChange={
														handleChangeVisibility
													}
													disabled={
														isUpdatingVisibility
													}
												/>
											</div>
										</div>
									</div>

									<div className="border-t border-zinc-100 pt-6">
										<h4 className="text-sm font-bold text-zinc-900">
											Tags de permissão
										</h4>
										<p className="mb-4 mt-1 text-sm text-zinc-500">
											Configure as permissões especiais do
											profissional na plataforma.
										</p>
										<div className="min-w-0 sm:max-w-md">
											<label className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-zinc-400">
												Permissões
											</label>
											<PermissionTagsEditor
												value={
													data.permission_tags ?? [
														'DEFAULT',
													]
												}
												onSave={
													handlePermissionTagsChange
												}
												disabled={
													isUpdatingPermissionTags
												}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Formulário de edição */}
						<div className="overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-sm">
							<div className="p-6 md:p-8">
								<ProfessionalForm
									clinics={clinics}
									onSubmit={onSubmit}
									isSubmitting={isPending}
									defaultValues={data}
								/>
							</div>
						</div>
					</>
				)}
			</div>
		</section>
	);
};

export { EditProfessional };
